import { useCallback, useEffect, useRef } from 'react';
import { syncService } from '../services/syncService';
import { useSyncStore } from '../stores/syncStore';
import { useTenantStore } from '../stores/tenantStore';
import { SyncType } from '../types/sync/enums';
import { useRefreshOnPanelRestored } from './useRefreshOnPanelRestored';
import { useRefreshOnVisible } from './useRefreshOnVisible';
import { useSync } from './useSync';

// Mount once (RootLayout): every mount asks the backend on its own. Panels stay open for days, so coming
// back to one asks again.
export function useDailySync(ready: boolean, initialSyncRequired: boolean): void {
    const { trigger, canSync } = useSync();
    const ciu = useTenantStore(s => s.ciu);
    const tenantSwitchedAt = useTenantStore(s => s.tenantSwitchedAt);
    const askedFor = useRef<string | null>(null);
    const allowed = ready && !initialSyncRequired && canSync && !!ciu;

    const check = useCallback(() => {
        if (!allowed || useSyncStore.getState().syncing) return;
        void syncService.getStatus()
            .then(status => status.dailySyncDue && !useSyncStore.getState().syncing ? trigger(SyncType.Full) : undefined)
            .catch(() => undefined);
    }, [allowed, trigger]);

    useEffect(() => {
        const company = `${ciu}|${tenantSwitchedAt}`;
        if (!allowed || askedFor.current === company) return;
        askedFor.current = company;
        check();
    }, [allowed, ciu, tenantSwitchedAt, check]);

    useRefreshOnVisible(check);
    useRefreshOnPanelRestored(check);
}
